import { useState } from 'react'
import { UserPlusIcon } from 'lucide-react'
import { toast } from 'sonner'
import { formatDecimalHours, localDate, monthRange } from '@coneno/c-sheet-shared'
import { NamePanel } from '@/components/forms/name-panel'
import { ListToolbar } from '@/components/layout/list-toolbar'
import { matchesStatus, type StatusFilter } from '@/lib/status-filter'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { usePanelParam } from '@/hooks/use-panel-param'
import { useCreatePerson, usePeople, useUpdatePerson } from '@/hooks/use-people'
import { useTimeEntries } from '@/hooks/use-timesheets'

export function PeoplePage(): React.JSX.Element {
  const people = usePeople()
  const entries = useTimeEntries()
  const createPerson = useCreatePerson()
  const updatePerson = useUpdatePerson()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('active')
  const [panel, setPanel] = usePanelParam('person')
  const editing =
    panel && panel !== 'new' ? (people.data?.find((p) => p.id === panel) ?? null) : null
  const month = monthRange(localDate())

  const minutes = new Map<string, { month: number; total: number }>()
  for (const entry of entries.data ?? []) {
    const totals = minutes.get(entry.personId) ?? { month: 0, total: 0 }
    totals.total += entry.durationMinutes
    if (entry.workDate >= month.from && entry.workDate <= month.to)
      totals.month += entry.durationMinutes
    minutes.set(entry.personId, totals)
  }
  const query = search.trim().toLowerCase()
  const visible =
    people.data?.filter(
      (person) =>
        matchesStatus(person.active, status) &&
        (!query || person.name.toLowerCase().includes(query))
    ) ?? []
  const activeCount = people.data?.filter((person) => person.active).length ?? 0

  function openPanel(value: string | null): void {
    setPanel(value)
    createPerson.reset()
    updatePerson.reset()
  }

  function submit({ name, active }: { name: string; active: boolean }): void {
    if (!editing) {
      createPerson.mutate(
        { name },
        {
          onSuccess: (person) => {
            toast(`${person.name} added`)
            openPanel(null)
          }
        }
      )
      return
    }
    updatePerson.mutate(
      { id: editing.id, input: { name, active } },
      {
        onSuccess: (person) => {
          if (editing.active && !person.active) {
            toast(`${person.name} deactivated`, {
              action: {
                label: 'Undo',
                onClick: () => updatePerson.mutate({ id: person.id, input: { active: true } })
              }
            })
          } else {
            toast(`${person.name} saved`)
          }
          openPanel(null)
        }
      }
    )
  }

  return (
    <PageBody>
      <PageHeader
        title="People"
        description={
          people.data
            ? `${activeCount} active · ${people.data.length - activeCount} inactive`
            : 'Loading…'
        }
        actions={
          <Button size="sm" onClick={() => openPanel('new')}>
            <UserPlusIcon data-icon="inline-start" />
            New person
          </Button>
        }
      >
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search people"
          status={status}
          onStatusChange={setStatus}
        />
      </PageHeader>

      {people.error && <p className="text-sm text-destructive">{people.error.message}</p>}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-120 text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-right font-medium">This month</th>
              <th className="px-3 py-2 text-right font-medium">All time</th>
            </tr>
          </thead>
          <tbody>
            {people.isPending ? (
              <tr>
                <td colSpan={3} className="p-3">
                  <Skeleton className="h-20 w-full" />
                </td>
              </tr>
            ) : visible.length ? (
              visible.map((person) => (
                <tr
                  key={person.id}
                  className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                  onClick={() => openPanel(person.id)}
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-left font-medium outline-none focus-visible:underline"
                      onClick={(event) => {
                        event.stopPropagation()
                        openPanel(person.id)
                      }}
                    >
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                        {person.name.slice(0, 1).toUpperCase()}
                      </span>
                      {person.name}
                    </button>
                    {!person.active && (
                      <Badge variant="secondary" className="ml-2">
                        Inactive
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatDecimalHours(minutes.get(person.id)?.month ?? 0)} h
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-muted-foreground">
                    {formatDecimalHours(minutes.get(person.id)?.total ?? 0)} h
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-3 py-10 text-center text-muted-foreground">
                  {people.data?.length
                    ? 'No people match.'
                    : 'No people yet. Add the first person.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <NamePanel
        open={panel === 'new' || Boolean(editing)}
        item={editing}
        noun="person"
        placeholder="e.g. Alice Meyer"
        inactiveHint="Inactive people stay on past entries but can’t get new ones."
        pending={createPerson.isPending || updatePerson.isPending}
        error={createPerson.error ?? updatePerson.error}
        onOpenChange={(open) => {
          if (!open) openPanel(null)
        }}
        onSubmit={submit}
      />
    </PageBody>
  )
}
